import React, { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    ButtonBase,
    Typography,
    Box,
    useTheme,
    useMediaQuery,
    Divider,
    Chip,
    Autocomplete,
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import config from "../config/config";
import { formatNumber } from "../utils/helpers";
import AddCustomerDialog from "./AddCustomerDialog";
import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "@mui/lab";

const parseNumber = (str) => {
    if (str === null || str === undefined || str === "") return 0;
    const cleanedStr = String(str).replace(/[^0-9]/g, "");
    const parsed = parseInt(cleanedStr, 10);
    return isNaN(parsed) ? 0 : parsed;
};

const AddOrderDialog = ({ open, onClose, product, onOrderAdded }) => {
    const imageUrl = product?.imageUrl || null;
    const img = `${config.backendUrl}${imageUrl}`;
    const { token } = useAuth();
    const { showToast } = useToast();
    const [customers, setCustomers] = useState([]);
    const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
    const [form, setForm] = useState({
        customerId: "",
        quantity: 1,
        paymentMethod: "Cash",
        discountPrice: 0,
        amountPaid: 0,
        discount: 0,
        priceAdjust: product?.price || 0, // New: priceAdjust
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [customerDebt, setCustomerDebt] = useState(0);
    const [projectedDebt, setProjectedDebt] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [customerPage, setCustomerPage] = useState(1);
    const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
    const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
    const [customerSearch, setCustomerSearch] = useState("");

    const handleChangeDiscount = (e) => {
        const raw = e.target.value.replace(/,/g, "");
        const num = parseInt(raw) || 0;
        setDiscount(num);
        setForm({ ...form, discountPrice: totalAmount, discount: discount });
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const totalAmount = useMemo(() => {
        if (!product) return 0;
        const priceAfterDiscount = form.priceAdjust - discount; // Use priceAdjust
        return priceAfterDiscount * form.quantity;
    }, [product, form.quantity, discount, form.priceAdjust]); // Add form.priceAdjust to dependencies
    // thêm vào hàng chờ
    const [draftOrders, setDraftOrders] = useState([]);
    // Tự động điền khách hàng trả tiền theo biến tổng TotalAmount
    const [autoFillAmount, setAutoFillAmount] = useState(true);
    useEffect(() => {
        if (autoFillAmount) {
            setForm((prev) => ({
                ...prev,
                amountPaid: totalAmount,
            }));
        }
    }, [totalAmount]);

    // Tắt tự động điền khi khách hàng nhập bằng bằng tay
    const handleChangeAmountPaid = (e) => {
        const val = parseNumber(e.target.value);
        setForm((prev) => ({ ...prev, amountPaid: val }));
        setAutoFillAmount(false);
    };

    // Tự động điền khách hàng trả tiền theo biến tổng discountPrice
    const [autoFillDiscountPrice, setAutoFillDiscountPrice] = useState(true);
    useEffect(() => {
        if (autoFillDiscountPrice) {
            setForm((prev) => ({
                ...prev,
                discountPrice: totalAmount,
            }));
        }
    }, [totalAmount]);

    useEffect(() => {
        if (open && product) {
            setCustomers([]);
            fetchCustomers(1, "");
            // Reset form when dialog opens
            setForm({
                customerId: "",
                quantity: 1,
                paymentMethod: "Cash",
                discountPrice: product.price || 0,
                amountPaid: product.price || 0,
                priceAdjust: product.price || 0, // Reset priceAdjust
            });
            setCustomerDebt(0);
            setProjectedDebt(0);
            setValidationErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, product]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setCustomers([]);
            fetchCustomers(1, customerSearch);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [customerSearch]);

    useEffect(() => {
        if (form.customerId) {
            const newDebt =
                customerDebt + (form.discountPrice - form.amountPaid);
            setProjectedDebt(newDebt);
        }
    }, [form.discountPrice, form.amountPaid, customerDebt, form.customerId]);

    const fetchCustomers = async (page, search = "") => {
        if (isFetchingCustomers) return;
        setIsFetchingCustomers(true);
        try {
            const res = await axios.get(`${config.backendUrl}/api/customers`, {
                headers: { "x-auth-token": token },
                params: { page, limit: 10, search },
            });
            const newCustomers = res.data.docs || [];
            setCustomers((prev) =>
                page === 1 ? newCustomers : [...prev, ...newCustomers]
            );
            setHasMoreCustomers(res.data.hasNextPage);
            setCustomerPage(page + 1);
        } catch (err) {
            console.error("Error fetching customers:", err);
            showToast("Không thể tải danh sách khách hàng.", "error");
        } finally {
            setIsFetchingCustomers(false);
        }
    };

    const handleClose = () => {
        onClose();
        setDraftOrders([]);
    };

    // Click avatar đổi sản phẩm chọn
    const handleChangeProduct = () => {
        // Ví dụ reset form hoặc mở dialog chọn sản phẩm
        setForm({
            ...form,
            quantity: 1,
            discount: 0,
        });
    };

    const handleCustomerAdded = (newCustomer) => {
        setCustomers((prev) => [...prev, newCustomer]);
        setForm({ ...form, customerId: newCustomer._id });
        setCustomerDebt(newCustomer.debt || 0);
        setAddCustomerDialogOpen(false);
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!form.customerId) {
            errors.customer = "Vui lòng chọn một khách hàng.";
        }
        if (form.quantity <= 0) {
            errors.quantity = "Số lượng phải lớn hơn 0.";
        }
        if (product && form.quantity > product.quantity) {
            errors.quantity = `Số lượng không được vượt quá số lượng tồn kho (${product.quantity}).`;
        }
        if (!form.paymentMethod) {
            errors.paymentMethod = "Phương thức thanh toán là bắt buộc";
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setIsSubmitting(true);
        try {
            const orderData = {
                items: [
                    {
                        product: product._id,
                        quantity: form.quantity,
                        lastQuantity: product.quantity, //lấy số lượng còn lại trong kho
                        price: product.price,
                        priceAdjust: form.priceAdjust, // Add priceAdjust to orderData
                        discountAmount: discount, // Add discountAmount
                        priceAfterDiscount: form.priceAdjust - discount,
                    },
                ],
                customerId: form.customerId,
                totalAmount: totalAmount,
                status: "Created",
                paymentMethod: form.paymentMethod,
                discountPrice:
                    form.discountPrice === "" ? 0 : form.discountPrice,
                amountPaid: form.amountPaid === "" ? 0 : form.amountPaid,
                discount: discount,
            };
            const res = await axios.post(
                `${config.backendUrl}/api/orders`,
                orderData,
                {
                    headers: { "x-auth-token": token },
                }
            );

            showToast("Đơn hàng đã được tạo thành công!", "success");
            if (onOrderAdded) {
                const order = res.data;
                const customer = customers.find(
                    (cust) => cust._id.toString() === order.customer
                );
                const convertOrder = {
                    ...order,
                    customer,
                };
                onOrderAdded(convertOrder);
            }
            handleClose();
        } catch (err) {
            console.error(err);
            const errorMessage =
                err.response?.data?.message ||
                "Đã xảy ra lỗi khi tạo đơn hàng.";
            showToast(errorMessage, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!product) return null;

    const PercenDiscount = (discount * 100) / form.priceAdjust;
    // const salePrice = product.price - discount;
    return (
        <>
            <Dialog
                open={open}
                fullScreen={isMobile}
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Thêm đơn hàng</DialogTitle>
                <DialogContent>
                    <Box display="flex" alignItems="center" mb={2}>
                        <ButtonBase onClick={handleChangeProduct}>
                            <Box
                                component="img"
                                src={img}
                                alt="Ảnh sản phẩm"
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 1,
                                    mr: 2,
                                }}
                            />
                        </ButtonBase>

                        <Box display="flex" flexDirection="column" flex={1}>
                            <Typography fontWeight="bold">
                                {product.name}
                            </Typography>

                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mt={0.5}
                            >
                                <Typography variant="caption">
                                    {product.sku}
                                </Typography>
                                <Chip size="small" label={product.quantity} />
                                <Chip size="small" label="KH đặt: 0" />
                            </Box>

                            <Typography
                                color="primary"
                                fontWeight="bold"
                                mt={0.5}
                            >
                                {product.price.toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                    <Autocomplete
                        options={[
                            {
                                _id: "add_new",
                                name: "Thêm khách hàng",
                                isAddNew: true,
                            },
                            ...customers,
                        ]}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) =>
                            option._id === value._id
                        }
                        value={
                            customers.find((c) => c._id === form.customerId) ||
                            null
                        }
                        onInputChange={(event, newInputValue, reason) => {
                            if (reason === "input") {
                                setCustomerSearch(newInputValue);
                            }
                        }}
                        ListboxProps={{
                            onScroll: (event) => {
                                const listboxNode = event.currentTarget;
                                if (
                                    listboxNode.scrollTop +
                                        listboxNode.clientHeight ===
                                        listboxNode.scrollHeight &&
                                    hasMoreCustomers &&
                                    !isFetchingCustomers
                                ) {
                                    fetchCustomers(
                                        customerPage,
                                        customerSearch
                                    );
                                }
                            },
                        }}
                        onChange={(event, newValue) => {
                            if (newValue?.isAddNew) {
                                setAddCustomerDialogOpen(true);
                            } else {
                                setForm({
                                    ...form,
                                    customerId: newValue ? newValue._id : "",
                                });
                                setCustomerDebt(newValue ? newValue.debt : 0);
                            }
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                {option.isAddNew ? (
                                    <Button
                                        variant="text"
                                        startIcon={<AddIcon />}
                                        sx={{
                                            color: "primary.main",
                                            fontWeight: "bold",
                                        }}
                                        fullWidth
                                    >
                                        Thêm khách hàng
                                    </Button>
                                ) : (
                                    <>
                                        {option.name}{" "}
                                        {option.phone && `(${option.phone})`}
                                    </>
                                )}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Khách hàng"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                error={!!validationErrors.customer}
                                helperText={validationErrors.customer}
                            />
                        )}
                    />
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography>Số lượng</Typography>
                        <TextField
                            required
                            value={form.quantity === 0 ? "" : form.quantity}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") {
                                    setForm({ ...form, quantity: 0 }); // Cho phép trống khi nhập
                                } else {
                                    setForm({
                                        ...form,
                                        quantity: parseInt(val, 10),
                                    });
                                }
                            }}
                            onBlur={() => {
                                if (!form.quantity || form.quantity < 1) {
                                    setForm({ ...form, quantity: 1 }); // Reset về 1 nếu trống hoặc nhỏ hơn 1 khi blur
                                }
                            }}
                            // type="number"
                            size="small"
                            inputProps={{
                                min: 1,
                                style: { textAlign: "center", padding: 4 },
                            }}
                            sx={{ width: 80 }}
                        />
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography>Đơn giá </Typography>
                        <TextField
                            variant="outlined"
                            size="small"
                            value={formatNumber(form.priceAdjust)}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    priceAdjust: parseNumber(e.target.value),
                                })
                            }
                            inputProps={{ min: 0 }}
                            sx={{ width: 120 }}
                        />
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography>
                            Giảm giá ({PercenDiscount.toFixed(1)} %)
                        </Typography>
                        <TextField
                            value={discount.toLocaleString()}
                            onChange={handleChangeDiscount}
                            type="text"
                            size="small"
                            inputProps={{
                                style: { textAlign: "center", padding: 4 },
                            }}
                            sx={{ width: 80 }}
                        />
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography>Giá bán</Typography>
                        <Typography>
                            {" "}
                            {(
                                form.priceAdjust - discount
                            ).toLocaleString()} ₫{" "}
                        </Typography>
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography fontWeight="bold">Thành tiền</Typography>
                        <Typography fontWeight="bold">
                            {totalAmount.toLocaleString()} ₫
                        </Typography>
                    </Box>

                    {/* New additions for debt and amount paid */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography>Công nợ hiện tại của khách hàng</Typography>
                        <Typography color="error" fontWeight="bold">
                            {customerDebt?.toLocaleString()} ₫
                        </Typography>
                    </Box>

                    <TextField
                        label="Số tiền thanh toán"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={form.amountPaid?.toLocaleString()}
                        onChange={handleChangeAmountPaid}
                        error={!!validationErrors.amountPaid}
                        helperText={validationErrors.amountPaid}
                    />

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography fontWeight="bold">
                            Công nợ sau thanh toán
                        </Typography>
                        <Typography
                            fontWeight="bold"
                            color={projectedDebt >= 0 ? "textPrimary" : "error"}
                        >
                            {projectedDebt.toLocaleString()} ₫
                        </Typography>
                    </Box>
                    {/* End new additions */}

                    {/* <Box display="flex" justifyContent="flex-end">
            <Button onClick={handleAddDraftOrder}>+ Thêm vào giỏ hàng</Button>
          </Box> */}

                    <Divider sx={{ my: 1 }} />
                    {draftOrders.length > 0 && (
                        <Box mb={2}>
                            {draftOrders.map((order, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    border="1px solid #ddd"
                                    borderRadius={1}
                                    p={1}
                                    mb={0.5}
                                >
                                    <Box display="flex" alignItems="center">
                                        <Box
                                            component="img"
                                            src={order.img}
                                            alt="Ảnh sản phẩm"
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 1,
                                                mr: 1,
                                            }}
                                        />
                                        <Typography>
                                            {order.product.name} ×{" "}
                                            {order.quantity}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        fontWeight="bold"
                                        color="primary"
                                    >
                                        {order.totalAmount.toLocaleString()} ₫
                                    </Typography>
                                </Box>
                            ))}
                            <Divider sx={{ my: 1 }} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Hủy</Button>
                    <LoadingButton
                        onClick={handleSubmit}
                        variant="contained"
                        loading={isSubmitting}
                        loadingIndicator="Đang xử lý..."
                    >
                        Thêm đơn hàng
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <AddCustomerDialog
                open={addCustomerDialogOpen}
                onClose={() => setAddCustomerDialogOpen(false)}
                onCustomerAdded={handleCustomerAdded}
            />
        </>
    );
};

export default AddOrderDialog;
